import { useState } from 'react';
import CurrencyInput from '../components/CurrencyInput';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function ToolRentalAssets({ data, onUpdate }: Props) {
  const [showRental, setShowRental] = useState((data.rentalPropertyCount || 0) > 0);
  const [showEIS, setShowEIS] = useState((data.eisSeisBalance || 0) > 0);

  // Calculate net monthly rental income
  const netMonthlyRentalIncome =
    (data.rentalMonthlyGrossIncome || 0) -
    (data.rentalMonthlyMortgageCosts || 0) -
    (data.rentalMonthlyRunningCosts || 0);

  const handleRentalToggle = (isYes: boolean) => {
    setShowRental(isYes);
    if (isYes && (data.rentalPropertyCount || 0) === 0) {
      onUpdate({ rentalPropertyCount: 1 });
    } else if (!isYes) {
      onUpdate({ rentalPropertyCount: 0 });
    }
  };

  const handleEISToggle = (isYes: boolean) => {
    setShowEIS(isYes);
  };

  return (
    <div className="space-y-6 p-6">
      {/* RENTAL PROPERTIES SECTION */}
      <div className="rounded-2xl bg-blue-50/50 p-6">
        <h2 className="mb-6 text-lg font-semibold uppercase tracking-wide text-ma-navy">
          Rental Properties
        </h2>

        {/* Radio Toggle: Do you own any rental properties? */}
        <div className="mb-6">
          <p className="mb-3 text-sm font-medium text-ma-navy">Do you own any rental properties?</p>
          <div className="flex gap-3">
            <button
              onClick={() => handleRentalToggle(false)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                !showRental
                  ? 'bg-ma-navy text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              No
            </button>
            <button
              onClick={() => handleRentalToggle(true)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                showRental
                  ? 'bg-ma-navy text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              Yes
            </button>
          </div>
        </div>

        {/* Rental Properties Details */}
        {showRental && (
          <div className="space-y-5">
            {/* Number of properties */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Number of Properties
              </label>
              <select
                value={data.rentalPropertyCount || 1}
                onChange={(e) => onUpdate({ rentalPropertyCount: parseInt(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-ma-navy focus:outline-none focus:ring-1 focus:ring-ma-navy"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4+</option>
              </select>
            </div>

            {/* Total equity across all */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Total Equity Across All Properties
              </label>
              <p className="mb-2 text-xs text-gray-600">
                Property values minus rental mortgages — adviser calculates
              </p>
              <CurrencyInput
                label="Total Equity Across All Properties"
                value={data.rentalEquityTotal || 0}
                onChange={(value) => onUpdate({ rentalEquityTotal: value })}
              />
            </div>

            {/* Total monthly gross rent */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Total Monthly Gross Rent
              </label>
              <CurrencyInput
                label="Total Monthly Gross Rent"
                value={data.rentalMonthlyGrossIncome || 0}
                onChange={(value) => onUpdate({ rentalMonthlyGrossIncome: value })}
              />
            </div>

            {/* Total monthly rental mortgages */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Total Monthly Rental Mortgages
              </label>
              <CurrencyInput
                label="Total Monthly Rental Mortgages"
                value={data.rentalMonthlyMortgageCosts || 0}
                onChange={(value) => onUpdate({ rentalMonthlyMortgageCosts: value })}
              />
            </div>

            {/* Total monthly running costs */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Total Monthly Running Costs
              </label>
              <p className="mb-2 text-xs text-gray-600">
                Maintenance, management fees, insurance
              </p>
              <CurrencyInput
                label="Total Monthly Running Costs"
                value={data.rentalMonthlyRunningCosts || 0}
                onChange={(value) => onUpdate({ rentalMonthlyRunningCosts: value })}
              />
            </div>

            {/* Auto-calculated summary */}
            <div className="mt-6 rounded-lg bg-white p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                Net Monthly Rental Income
              </p>
              <p
                className={`text-2xl font-bold ${
                  netMonthlyRentalIncome >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(netMonthlyRentalIncome)}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Gross rent − Mortgages − Running costs
              </p>
            </div>
          </div>
        )}
      </div>

      {/* EIS / SEIS INVESTMENTS SECTION */}
      <div className="rounded-2xl bg-blue-50/50 p-6">
        <h2 className="mb-6 text-lg font-semibold uppercase tracking-wide text-ma-navy">
          EIS / SEIS Investments
        </h2>

        {/* Radio Toggle: Do you have any EIS or SEIS investments? */}
        <div className="mb-6">
          <p className="mb-3 text-sm font-medium text-ma-navy">
            Do you have any EIS or SEIS investments?
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => handleEISToggle(false)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                !showEIS
                  ? 'bg-ma-navy text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              No
            </button>
            <button
              onClick={() => handleEISToggle(true)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                showEIS
                  ? 'bg-ma-navy text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              Yes
            </button>
          </div>
        </div>

        {/* EIS / SEIS Details */}
        {showEIS && (
          <div className="space-y-5">
            {/* Approximate total value */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Approximate Total Value
              </label>
              <CurrencyInput
                label="Approximate Total Value"
                value={data.eisSeisBalance || 0}
                onChange={(value) => onUpdate({ eisSeisBalance: value })}
              />
              <p className="mt-2 text-xs text-gray-600">
                EIS/SEIS investments are illiquid and shown on your balance sheet only. They are not
                included in growth projections.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
